import { useRef } from "react";
import { Button, Modal, Typography } from "@/components/atoms";
import { ABOVE_VEIVO_WALK_AROUND } from "@/helpers/veivoOverlay";

/** What the walk-around's ? and its "Where is the VIN?" ask for. */
export type VeivoHelpTopic = "walkAround" | "vin";

const HELP: Record<VeivoHelpTopic, { title: string; intro: string; points: string[] }> = {
  walkAround: {
    title: "How the walk-around works",
    intro: "The camera photographs the vehicle from four angles as you walk around it. No video is recorded.",
    points: [
      "Start at the front of the vehicle, a few steps back, with the whole vehicle in the frame.",
      "Walk slowly around the vehicle and back to the front, keeping it in the frame the whole way.",
      "Hold still when the countdown appears: that is when a photo is taken.",
      "Daylight or a well-lit area works best. Keep people and other vehicles out of the frame.",
    ],
  },
  vin: {
    title: "Where is the VIN?",
    intro:
      "The VIN (vehicle identification number) is 17 letters and numbers. Photograph it on the vehicle itself: it must match the VIN on the registration document.",
    points: [
      "At the base of the windscreen on the driver's side, seen from outside.",
      "On a plate or sticker in the driver's door frame.",
      "Stamped on the chassis under the bonnet, on some vehicles.",
    ],
  },
};

interface VeivoHelpModalProps {
  /** Null when closed. */
  topic: VeivoHelpTopic | null;
  onClose: () => void;
}

/**
 * The help the Veivo walk-around asks its host for (`onHelp`). It opens over
 * the walk-around, which is full-screen on phones, so it sits above it.
 */
export const VeivoHelpModal = ({ topic, onClose }: VeivoHelpModalProps) => {
  // Kept after closing so the text does not vanish while the modal fades out.
  const shown = useRef<VeivoHelpTopic>("walkAround");
  if (topic) shown.current = topic;
  const help = HELP[shown.current];

  return (
    <Modal
      open={Boolean(topic)}
      onClose={onClose}
      title={help.title}
      description={help.intro}
      variant="web-small"
      zIndexClassName={ABOVE_VEIVO_WALK_AROUND}
      renderButtons={() => (
        <Button variant="primary" size="md" onClick={onClose}>
          Got it
        </Button>
      )}
    >
      <ul className="flex list-disc flex-col gap-2 pl-5">
        {help.points.map((point) => (
          <li key={point}>
            <Typography variant="paragraph">{point}</Typography>
          </li>
        ))}
      </ul>
    </Modal>
  );
};
